import FullCalendar from '@fullcalendar/react'; // must go before plugins
import dayGridPlugin from '@fullcalendar/daygrid'; // a plugin!
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction'; // needed for dayClick
import { DatesSetArg, EventClickArg } from '@fullcalendar/core';
import { CalendarListing } from '@/types';
import { Box } from '@silverstein-properties/inspirelabs-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { StyledCalendar } from './StyledCalendar';
import { EventItem } from './EventItem';
import { CircularProgress, useTheme } from '@mui/material';
import { groupEventsByTimeOverlap } from '@/utils/schedulingUtils';
import { VIEWS } from '@/constants';
import { MoreEventsPopover } from '../MoreEventsPopover';
import { EventImpl } from '@fullcalendar/core/internal';

export type CalendarViewProps = {
  height: string;
  width: string;
  handleNewEventClick: (arg: DateClickArg) => void;
  handleEventClick: (id: string) => void;
  onDatesSet?: (dateInfo: DatesSetArg) => void;
  events?: CalendarListing[];
  selectedView: string;
  selectedDate: Date;
  isLoading?: boolean;
};

export const CalendarView = ({
  handleNewEventClick,
  handleEventClick,
  height,
  width,
  events,
  selectedView,
  selectedDate,
  isLoading,
}: CalendarViewProps) => {
  const theme = useTheme();
  const [isCalendarLoading, setIsCalendarLoading] = useState(true);
  const [moreEventsPopover, setMoreEventsPopover] = useState<{
    anchorEl: HTMLElement;
    event: EventImpl;
  } | null>(null);

  const onEventClick = (e: EventClickArg) => {
    if (e.view.type !== 'listMonth') {
      handleEventClick(e.event._def.publicId);
    }
  };

  const calendarRef = useRef<FullCalendar>(null);

  useEffect(() => {
    if (calendarRef.current) {
      calendarRef.current.getApi().changeView(selectedView || 'dayGridMonth');
    }
  }, [selectedView]);

  useEffect(() => {
    if (calendarRef.current) {
      calendarRef.current.getApi().gotoDate(selectedDate);
    }
  }, [selectedDate]);

  const groupedEvents = useMemo(() => {
    if (selectedView === VIEWS.DAY || selectedView === VIEWS.WEEK) {
      return groupEventsByTimeOverlap(events || []);
    }
    return events || [];
  }, [events, selectedView]);

  return (
    <StyledCalendar>
      <Box
        sx={{
          height,
          width,
          position: 'relative',
        }}
      >
        {(isLoading || isCalendarLoading) && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: theme.palette.grey[100],
              opacity: 0.5,
              zIndex: 1000,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <FullCalendar
          aspectRatio={1.45}
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          editable={true}
          initialView={selectedView}
          loading={status => {
            setIsCalendarLoading(status);
          }}
          views={{
            month: {
              fixedWeekCount: false, // allow variable week count
              dayMaxEventRows: 3, // set max number of events showing a day to 3 before only 2 and the text '+2 more'
              eventLimitClick: 'popover', // display list of events when limit is reached
              showNonCurrentDates: false, // hide dates from previous and next months
            },
            week: {
              allDaySlot: false,
              slotDuration: '00:15:00',
              slotEventOverlap: false,
            },
            day: {
              displayEventEnd: false,
              allDaySlot: false,
              slotEventOverlap: false,
              slotDuration: '00:15:00',
            },
          }}
          dateClick={handleNewEventClick}
          eventClick={onEventClick}
          headerToolbar={{
            left: '',
            center: '',
            right: '',
          }}
          events={groupedEvents}
          eventContent={({ event }) => (
            <EventItem
              event={{
                ...(event.extendedProps as CalendarListing),
                start: event.start || '',
              }}
              onMoreEventsClick={anchor =>
                setMoreEventsPopover({ anchorEl: anchor, event })
              }
            />
          )}
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            meridiem: 'short',
          }}
          eventDisplay="block"
          scrollTime="09:00:00" //in week or day view, autoscroll to 9 AM(or whenever we'd like).
          eventStartEditable={false}
        />
      </Box>
      <MoreEventsPopover
        anchorEl={moreEventsPopover?.anchorEl}
        event={moreEventsPopover?.event}
        onClose={() => setMoreEventsPopover(null)}
      />
    </StyledCalendar>
  );
};
